<template>
  <div>
    <div ref="realgridTree" :style="`height: ${height}px;`"></div>
  </div>
</template>

<script>
import Message from '@/utils/message'
import EventHandler from '@/utils/eventHandler'
import RealGrid from 'realgrid/dist/main'

let defaultOptions = {
  tree: {
    idCol: 'id', // 트리 구조를 만들 때 사용 할 아이디 컬럼
    prntIdCol: 'prnt_id', // 트리 구조를 만들 때 사용 할 부모 아이디 컬럼
    levelCol: 'level', // 트리 구조를 만들 때 사용할 레벨 컬럼
    childCol: 'children', // 트리 구조를 만들 때 자식 노드들이 들어갈 컬럼
    sortCol: 'sort' // 트리 구조를 만들 때 정렬을 위한 컬럼
  },
  treeView: {
    edit: {
      appendable: true,
      insertable: true,
      deletable: true
    }
  },
  treeDataProvider: {
    softDeleting: true,
    deleteCreated: true // 추가되고 저장되기 전인 row 는 바로 삭제한다.
  }
}

export default {
  name: 'RealgridTree',
  // 기본 사용 내역 리스트 업
  props: {
    // 조회 전용 API
    retrieveApi: Function,
    // 저장 (추가/수정/삭제) 대응하는 전용 API
    saveApi: Function,
    // RealgridTree를 정의하기 위한 옵션
    // RealgridTree를 setOptions()에 사용되는 형식으로 구성하여 전달해야 한다.
    // treeView와 this.treeDataProvider 각각 2개 영역으로 나눠져야 한다.
    options: {
      type: Object,
      required: false,
      default() {
        return defaultOptions
      }
    },
    // 조회 시, 사용되는 옵션 값
    retrieveData: Object,
    // Realgrid Data Provider에 사용되는 기본 열 정의
    fields: Array,
    // Realgrid Grid View에 사용되는 기본 열 정의
    columns: Array,
    // Realgrid Grid의 Height 지정, 부모에서 값을 지정하지 않으면, 500으로 자동 셋팅 된다.
    height: {
      type: Number,
      required: false,
      default: 500
    }
  },
  data() {
    return {
      treeView: null,
      treeDataProvider: null,
      // 조회 시, 사용했던 조회 조건 저장 부분
      // Save 사용 간 사용하게 된다.
      retrieveOriginData: {},
      inited: false,
      contextMenuArray: [
        { label: 'Column Filter 켜키', type: 'normal', tag: 'filter_on' },
        { label: 'Column Filter 끄기', type: 'normal', tag: 'filter_off' },
        { label: '-' }, // 메뉴 구분선 추가
        { label: 'Excel Export', type: 'normal', tag: 'export' }
      ]
    }
  },
  mounted: function() {
    // 그리드를 그려준다.
    this.initGrid()
  },
  methods: {
    // retrieveApi를 통해 자동으로 그리드의 내용을 조회하여 반영한다.
    retrieve() {
      if (typeof this.retrieveApi !== 'function') return
      let reqObj = {}

      if (this.retrieveData) reqObj = this.retrieveData
      // this.options.tree 의 값 중 비어있는 값을 채워주기 위해 assign 사용
      // this.options.tree 내부의 값들은 모두 필수값이므로 하나라도 비어있으면 안된다.
      this.options.tree = Object.assign(defaultOptions.tree, this.options.tree)

      // 조회 시, 사용했던 조건 데이터들은 retrieveOriginData에 저장되어, 차후 Save 시 사용된다.
      this.retrieveOriginData = reqObj

      // 데이터를 가져오는 retrieveApi를 API 실행
      return new Promise((resolve, reject) => {
        this.retrieveApi(reqObj)
          .then(response => {
            // 1레벨 데이터도 root 레벨의 자식이기 때문에 responseList 자체가 root 레벨이다.
            // root 레벨의 children 에 1레벨부터의 데이터가 들어간다.
            var responseList = {}
            if (response) {
              responseList[this.options.tree.childCol] = this.setTreeData(
                response
              )
              // RealGrid에 조회한 데이터를 넣는다.
              this.treeDataProvider.fillJsonData(responseList, {
                rows: this.options.tree.childCol
              })
            } else {
              // RealGrid에 조회한 데이터를 넣는다.
              this.treeDataProvider.fillJsonData([], {
                rows: this.options.tree.childCol
              })
            }
            // 한번이라도 조회되면 inited 값을 true 로 변경해 추가,삭제,저장 메소드를 수행할 수 있게한다.
            this.inited = true
            // 전체 트리를 접어준다.
            this.treeView.collapseAll()
            resolve()
          })
          .catch(error => {
            reject(error)
          })
      })
    },
    // 전체 데이터를 넘겨 tree 구조를 만들어준다.
    setTreeData(treeData) {
      var tree = {}
      tree[this.options.tree.childCol] = []

      treeData.forEach(treeItem => {
        this.resolveTreeData(treeItem, tree, tree) // 함수에 각 데이터를 넣어 트리를 만든다.
      })
      return tree[this.options.tree.childCol] // 만들어진 트리를 반환한다.
    },
    // 메뉴 트리를 만들기 위해 사용하는 함수로 src\store\modules\menu.js 내부의 메뉴 트리를 만드는 함수와 동일하다.
    resolveTreeData(treeItem, targetTreeItem, tree) {
      if ((treeItem[this.options.tree.levelCol] | 0) === 1) {
        tree[this.options.tree.childCol].push(treeItem)
        return
      }

      if (
        treeItem[this.options.tree.prntIdCol] ===
        targetTreeItem[this.options.tree.idCol]
      ) {
        if (!targetTreeItem[this.options.tree.childCol]) targetTreeItem[this.options.tree.childCol] = []
        targetTreeItem[this.options.tree.childCol].push(treeItem)
        return
      }
      if (targetTreeItem[this.options.tree.childCol]) {
        targetTreeItem[this.options.tree.childCol].forEach(
          newTargetTreeItem => {
            this.resolveTreeData(treeItem, newTargetTreeItem, tree)
          }
        )
      }
    },
    // 해당 그리드에 새로운 Row를 추가한다.
    addRootRow() {
      // 조회 되지 않는 그리드에서는 해당 기능을 사용할 수 없다.
      if (!this.inited) return
      // 수정 도중이면 현재 상황을 정리한다.
      // commit은 수정 줄이 수정 상태 그대로, cancel은 수정 이전 상태로 변경
      this.treeView.commit()
      // -1이 root 레벨, root 레벨의 자식 추가(1레벨)
      // 추가된 row의 itemIndex 값 리턴
      var addRowIdx = this.treeDataProvider.addChildRow(-1, [])

      // 추가된 노드에 포커스 맞춤
      this.treeView.setCurrent({
        itemIndex: this.treeView.getItemIndex(addRowIdx)
      })
    },
    // 해당 그리드에 새로운 Row를 추가한다.
    addChildRow() {
      // 조회 되지 않는 그리드에서는 해당 기능을 사용할 수 없다.
      if (!this.inited) return
      // 수정 도중이면 현재 상황을 정리한다.
      // commit은 수정 줄이 수정 상태 그대로, cancel은 수정 이전 상태로 변경
      this.treeView.commit()
      // 트리의 선택된 셀 정보를 가져온다.
      var current = this.treeView.getCurrent()
      // 해당 셀의 자식 노드 수를 가져온다.
      var childCount = this.treeDataProvider.getChildCount(current.dataRow)
      // 자식의 가장 마지막 위치에 row 를 추가한다.
      var addRowIdx = this.treeDataProvider.insertChildRow(
        current.dataRow,
        childCount,
        []
      )

      // 자식 노드 추가시 부모 노드의 트리 펼침
      this.treeView.expand(current.itemIndex)

      // 추가된 노드에 포커스 맞춤
      this.treeView.setCurrent({
        itemIndex: this.treeView.getItemIndex(addRowIdx)
      })
    },
    // 선택된 Current Row에 삭제 표시를 한다.
    // 차후, 저장 시 삭제 대상이 된다.
    deleteRow() {
      // 조회 되지 않는 그리드에서는 해당 기능을 사용할 수 없다.
      if (!this.inited) return
      this.treeView.commit()

      // 옵션이 true일 경우 묻지않고 삭제 대상이 된다.
      this.treeView.deleteSelection(true)
    },
    // saveApi에 정의한 API를 호출한다.
    // 조회 때 저장한 조건 데이터를 가져와 사용한다.
    save() {
      // 조회 되지 않는 그리드에서는 해당 기능을 사용할 수 없다.
      if (!this.inited) return
      // commit은 수정 줄이 수정 상태 그대로, cancel은 수정 이전 상태로 변경
      this.treeView.commit()

      // saveApi에 API 매칭되어야 기능을 사용할 수 있다.
      if (typeof this.saveApi !== 'function') return
      var gridSaveData = {
        updated: [],
        created: [],
        deleted: []
      }
      // 각 상태 별 값을 가져온다.
      // RealGrid에서 getStateRows를 통해 각 상태별 dataprovier 내 index 정보를 가져온다.
      var updatedRows = this.treeDataProvider.getStateRows('updated')
      var createdRows = this.treeDataProvider.getStateRows('created')
      var deletedRows = this.treeDataProvider.getStateRows('deleted')

      // index 정보를 기준으로 각 실제 값을 가져온다.
      updatedRows.forEach((row, idx) => {
        gridSaveData.updated.push(this.treeDataProvider.getJsonRow(row))
      })
      createdRows.forEach((row, idx) => {
        gridSaveData.created.push(this.treeDataProvider.getJsonRow(row))
      })
      deletedRows.forEach((row, idx) => {
        gridSaveData.deleted.push(this.treeDataProvider.getJsonRow(row))
      })
      gridSaveData.retrieveData = this.retrieveOriginData

      // 해당 데이터는 아래와 같이 추가된다.
      // 이 값들은 Payload 곳에 저장되어 서버에 전송 되는 것을 권장한다.
      /*
      {
        retrieveData: {}, // 조회 시, 사용했던 조건 데이터, 서버에도 전송된다.
        updated: [], // 수정 했던 Row 데이터들이 들어간다.
        created: [], // 추가 했던 Row 데이터들이 들어간다.
        deleted: []  // 삭제 했던 Row 데이터들이 들어간다.
      }
      */
      // 전송
      return new Promise((resolve, reject) => {
        // 조회 시, 조건 파라미터들은 this.retrieveOriginData 저장되어 있으며,
        // 이 값을 저장 시에도 조건으로 사용한다.
        this.saveApi(gridSaveData)
          .then(response => {
            // 저장 완료되면, 그리드를 재 조회 한다.
            this.retrieve()
            resolve()
          })
          .catch(error => {
            reject(error)
          })
      })
    },
    realgridLoadHandler() {
      return new Promise((r, e) => {
        EventHandler.once(() => {
          r()
        }, 'loaded-realgridjs')
      })
    },
    initGrid() {
      // await this.realgridLoadHandler()
      if (!this.fields) {
        console.warn(
          '[UI Framework] Realgrid Tree Component 내 fields를 정의하지 않았습니다.'
        )
        return
      }
      if (!this.columns) {
        console.warn(
          '[UI Framework] Realgrid Tree Component 내 columns를 정의하지 않았습니다.'
        )
        return
      }
      // eslint-disable-next-line
      // this.treeDataProvider = new RealGridJS.LocalTreeDataProvider()
      this.treeDataProvider = new RealGrid.LocalTreeDataProvider()
      // eslint-disable-next-line
      // this.treeView = new RealGridJS.TreeView(this.$refs.realgridTree)
      this.treeView = new RealGrid.TreeView(this.$refs.realgridTree)
      this.treeView.setDataSource(this.treeDataProvider)

      // this.treeView에 옵션을 적용한다.
      if (this.options && this.options.treeView) {
        this.treeView.setOptions(this.options.treeView)
      } else {
        // options 값이 설정되지 않으면 아래의 값으로 설정된다.
        this.treeView.setOptions(defaultOptions.treeView)
      }

      // 필터 옵션 설정
      this.treeView.setFilteringOptions({
        selector: {
          showButtons: true, // 버튼 표시 여부
          acceptText: '확인', // 필터 적용 버튼 text
          cancelText: '취소' //  취소 버튼 text
        }
      })

      this.treeView.setContextMenu(this.contextMenuArray) // 컨텍스트 메뉴 설정

      // 컨텍스트 메뉴가 실행될 때 호출되는 함수, false 를 리턴하면 컨텍스트 메뉴를 실행하지 않는다.
      this.treeView.onContextMenuPopup = (cgrid, x, y, elementName) => {
        // 조회 전이거나, 조회후에 그리드 데이터가 없으면 컨텍스트 메뉴 실행하지 않음
        if (!this.inited || this.treeDataProvider.getRowCount() <= 0) return false
        // 헤더, 푸터, 그룹핑 패널 영역에서는 컨텍스트 메뉴 실행하지 않음
        if (elementName !== ('GridBody' && 'DataCell')) return false
      }

      this.treeView.onContextMenuItemClicked = (cgrid, menuItem, index) => {
        // 선택된 contextmenu 항목에 따라 처리
        switch (menuItem.tag) {
          case 'export': // 엑셀 다운로드
            this.treeView.exportGrid({
              type: 'excel',
              target: 'local',
              fileName: 'gridExportSample.xlsx', // 저장될 파일 name
              progressMessage: '엑셀 다운로드중입니다.', // 엑셀 다운로드 하는 동안 progressMessage 표시
              showProgress: true, // 엑셀 다운로드 하는 동안 progressBar 표시
              indicator: 'visible', // indidator 영역 표시
              header: 'visible', // 헤더 표시
              footer: 'hidden', // footer 표시하지 않음
              allColumns: true, // visible : false 인 행도 표시할 것인지 여부
              done: () => {
                // 내보내기 완료 후 실행되는 함수
                Message.success('엑셀 다운로드가 완료되었습니다.')
              }
            })
            break
          case 'filter_on': // Column Filter 켜키
            var distinctValues = this.treeDataProvider.getDistinctValues(
              index.column
            ) // 지정된 컬럼의 데이터들을 중복을 제거하고 가져온다.
            var filters = []
            for (var value of distinctValues) {
              // 중복을 제거한 데이터를 넣어준다.
              // name: 사용자에게 보여질 값
              // criteria: 해당 필터 선택시 필터 조건
              filters.push({ name: value, criteria: "value = '" + value + "'" })
            }
            this.treeView.setColumnFilters(index.column, filters) // 선택된 컬럼에 필터 셋팅
            break
          case 'filter_off': // Column Filter 끄기
            this.treeView.clearColumnFilters(index.column) // 해당 컬럼의 필터 설정을 해제한다
            break
        }
      }
      // dataProvicder에 옵션을 적용한다.
      if (this.options && this.options.treeDataProvider) {
        // this.treeDataProvider에 사전에 정의한 옵션을 적용한다.
        this.treeDataProvider.setOptions(this.options.treeDataProvider)
      } else {
        // this.treeDataProvider에 기본 옵션을 적용한다.
        this.treeDataProvider.setOptions(defaultOptions.treeDataProvider)
      }

      // 정의해준 필드와 컬럼을 넣어준다.
      this.treeDataProvider.setFields(this.fields)
      this.treeView.setColumns(this.columns)

      // realgrid가 적용 완료됨을 전달
      this.$emit('loaded')
    },
    getTreeView() {
      return this.treeView
    },
    getTreeDataProvider() {
      return this.treeDataProvider
    },
    excelDown(fileName) {
      if (fileName) {
        fileName = fileName.trim()
      }
      this.treeView.commit()
      this.treeView.exportGrid({
        type: 'excel',
        target: 'local',
        fileName: fileName ? fileName + '.xlsx' : 'gridExport.xlsx', // 저장될 파일 name
        progressMessage: '엑셀 다운로드중입니다.', // 엑셀 다운로드 하는 동안 progressMessage 표시
        showProgress: true, // 엑셀 다운로드 하는 동안 progressBar 표시
        indicator: 'visible', // indidator 영역 표시
        header: 'visible', // 헤더 표시
        footer: 'hidden', // footer 표시하지 않음
        allColumns: true, // visible : false 인 행도 표시할 것인지 여부
        done: () => {
          // 내보내기 완료 후 실행되는 함수
          Message.success('엑셀 다운로드가 완료되었습니다.')
        }
      })
    }
  },
  beforeDestroy: function() {
    /**
     * RealGrid를 사용할 경우, lifecycle 내 beforeDestroy 항목에서,
     * RealGrid가 사용한 리소스를 해제해야 하는 작업이 필요합니다.
     */

    // 데이터 초기화
    this.treeDataProvider.clearRows()

    // grid, provider 초기화
    this.treeView.destroy()
    this.treeDataProvider.destroy()

    // LocalDataProvider와 TreeView 객체 초기화
    this.treeView = null
    this.treeDataProvider = null
  }
}
</script>

<style>
</style>
